import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { ROLE } from '../constants/common';

/** Creator (monthly / PPV) — UI tab id → path under /api/v1 */
const CREATOR_TAB_TO_PATH = {
  priority_messages: 'creator/notifications/priority-messages/active',
  sales_tips: 'creator/notifications/sales-tips/active',
  subscribers: 'creator/notifications/audience/active',
  followers: 'creator/notifications/audience/active',
  payouts: 'creator/notifications/payouts/active',
  tags: 'creator/notifications/tags/active',
  promotion_campaign: 'creator/notifications/promotions/active',
};

/** Creator archived — one endpoint per category (Archived tab sections). */
const CREATOR_TAB_TO_ARCHIVED_PATH = {
  priority_messages: 'creator/notifications/priority-messages/archived',
  sales_tips: 'creator/notifications/sales-tips/archived',
  subscribers: 'creator/notifications/audience/archived',
  followers: 'creator/notifications/audience/archived',
  payouts: 'creator/notifications/payouts/archived',
  tags: 'creator/notifications/tags/archived',
  promotion_campaign: 'creator/notifications/promotions/archived',
};

/** Subscriber (fan) — same response shape as creator tabs */
const USER_TAB_TO_PATH = {
  priority_messages: 'notifications/messages/active',
  new_posts: 'notifications/posts/active',
  subscriptions: 'notifications/subscription/active',
  purchases_tips: 'notifications/paymentAndTips/active',
  promotions: 'notifications/promotions/active',
};

/** Subscriber archived — one endpoint per category (Archived tab sections). */
const USER_TAB_TO_ARCHIVED_PATH = {
  priority_messages: 'notifications/messages/archived',
  new_posts: 'notifications/posts/archived',
  subscriptions: 'notifications/subscription/archived',
  purchases_tips: 'notifications/paymentAndTips/archived',
  promotions: 'notifications/promotions/archived',
};

// const GROUPED_QUERY_TABS = new Set([
//   'sales_tips',
//   'subscribers',
//   'followers',
//   'tags',
//   'new_posts',
//   'purchases_tips',
// ]);

function tabPathMapForRole(role, { archived = false } = {}) {
  if (role === ROLE.USER) {
    return archived ? USER_TAB_TO_ARCHIVED_PATH : USER_TAB_TO_PATH;
  }
  if (role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT) {
    return archived ? CREATOR_TAB_TO_ARCHIVED_PATH : CREATOR_TAB_TO_PATH;
  }
  return null;
}

/** Subscriber — PATCH .../:id/viewed */
const USER_TAB_VIEW_PATH = {
  priority_messages: (id) => `notifications/messages/${id}/viewed`,
  new_posts: (id) => `notifications/posts/${id}/viewed`,
  subscriptions: (id) => `notifications/subscription/${id}/viewed`,
  purchases_tips: (id) => `notifications/paymentAndTips/${id}/viewed`,
  promotions: (id) => `notifications/promotions/${id}/viewed`,
};

/** Creator — PATCH .../:id/viewed */
const CREATOR_TAB_VIEW_PATH = {
  priority_messages: (id) => `creator/notifications/priority-messages/${id}/viewed`,
  sales_tips: (id) => `creator/notifications/sales-tips/${id}/viewed`,
  subscribers: (id) => `creator/notifications/audience/${id}/viewed`,
  followers: (id) => `creator/notifications/audience/${id}/viewed`,
  tags: (id) => `creator/notifications/tags/${id}/viewed`,
  payouts: (id) => `creator/notifications/payouts/${id}/viewed`,
  promotion_campaign: (id) => `creator/notifications/promotions/${id}/viewed`,
};

function viewedPathForTab(tabId, id, role) {
  if (!id || tabId === 'archived') return null;
  if (role === ROLE.USER) {
    const fn = USER_TAB_VIEW_PATH[tabId];
    return fn ? fn(id) : null;
  }
  if (role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT) {
    const fn = CREATOR_TAB_VIEW_PATH[tabId];
    return fn ? fn(id) : null;
  }
  return null;
}

class ActivityService {
  /**
   * @param {string} tabId - Activity filter tab
   * @param {{ page?: number, limit?: number }} params
   * @param {number} role - ROLE.CREATOR_SUB | ROLE.CREATOR_CONTENT | ROLE.USER
   */
  async getActivityNotifications(tabId, params = {}, role) {
    const archived = !!params.archived;
    const TAB_TO_PATH = tabPathMapForRole(role, { archived });
    if (!TAB_TO_PATH) {
      return {
        success: false,
        message: 'Activity is not available for this account type',
        data: null,
      };
    }

    const path = TAB_TO_PATH[tabId];
    if (!path) {
      return {
        success: false,
        message: 'Unknown activity tab',
        data: null,
      };
    }

    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    let url;

    if (archived) {
      const search = new URLSearchParams({
        limit: String(limit),
        page: String(Math.max(1, page)),
      });
      if (params.grouped === false) {
        search.set('grouped', 'false');
      }
      url = `${path}?${search.toString()}`;
    } else {
      const search = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      const grouped =
        typeof params.grouped === 'boolean' ? params.grouped : true;
      search.set('grouped', grouped ? 'true' : 'false');
      url = `${path}?${search.toString()}`;
    }
    try {
      const response = await apiClient.get(url);
      return ResponseHandler.handleSuccess(response, 'Activity fetched successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to fetch activity');
    }
  }

  /**
   * Mark a single notification as viewed (when it scrolls into view).
   * @param {string} tabId
   * @param {string} notificationId - API _id
   * @param {number} role
   */
  async markNotificationViewed(tabId, notificationId, role) {
    const path = viewedPathForTab(tabId, notificationId, role);
    if (!path) {
      return {
        success: false,
        message: 'View tracking not available for this tab',
        data: null,
      };
    }
    try {
      const response = await apiClient.patch(path);
      return ResponseHandler.handleSuccess(response, 'Marked as viewed');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to mark as viewed');
    }
  }
}

export default new ActivityService();
export { CREATOR_TAB_TO_PATH, USER_TAB_TO_PATH };
